/**
 * HomeStatusComposer — quick Oppdatering composer for the dashboard homepage.
 *
 * Subject (Prosjekt / Kunde) is optional: the user can write or dictate and publish
 * without a selection. AI may suggest projects as non-blocking chips; accepting a
 * chip binds the subject. Manual Prosjekt/Kunde selectors remain available.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PencilSquareIcon,
  FolderIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { CustomerSelector } from "../../utils/moduleSelectors/customerSelector";
import {
  fetchStatusUpdates,
  isStatusUpdateModuleUnavailable,
} from "../../../api/statusUpdates";
import { StatusUpdateComposer } from "./StatusUpdateComposer";
import { StatusUpdateUnavailableNotice } from "./StatusUpdateUnavailableNotice";
import { TWclassNames } from "../../utils/Div";

const SUBJECT_TYPES = [
  { key: "project", labelKey: "Prosjekt", Icon: FolderIcon },
  { key: "customer", labelKey: "Kunde", Icon: BuildingOffice2Icon },
];

export function HomeStatusComposer({ dashboardDesign = false }) {
  const { t } = useTranslation();
  const [subjectType, setSubjectType] = useState(null);
  const [selected, setSelected] = useState(null); // { id, label }
  const [openSelector, setOpenSelector] = useState(null); // "project" | "customer" | null
  const [justPosted, setJustPosted] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [probing, setProbing] = useState(false);
  const requestIdRef = useRef(0);

  const probeAvailability = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    setProbing(true);
    try {
      const res = await fetchStatusUpdates({ mine: true, page: 1, perPage: 1 });
      if (reqId !== requestIdRef.current) return;
      if (isStatusUpdateModuleUnavailable(res)) {
        setUnavailable(true);
        return;
      }
      // Successful probe recovers a prior unavailable latch (transient 404, etc.).
      if (!res?.__error) setUnavailable(false);
    } finally {
      if (reqId === requestIdRef.current) setProbing(false);
    }
  }, []);

  // Probe on mount so the home card shows unavailable before the first publish attempt.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const reqId = ++requestIdRef.current;
      const res = await fetchStatusUpdates({ mine: true, page: 1, perPage: 1 });
      if (cancelled) return;
      if (reqId !== requestIdRef.current) return;
      if (isStatusUpdateModuleUnavailable(res)) setUnavailable(true);
      else if (!res?.__error) setUnavailable(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const chooseType = (key) => {
    setSubjectType(key);
    setSelected(null);
    setOpenSelector(key);
  };

  const handleSelected = (event) => {
    const target = event?.target ?? {};
    const data = target.data ?? {};
    const object = target.object ?? data.object ?? data;
    const id = target.value ?? data.id ?? object?.id;
    if (!id) return;
    setSelected({
      id,
      label: data.primary || object?.displayTitle || object?.name || `#${id}`,
    });
    setOpenSelector(null);
  };

  const handleAcceptSuggestedProject = (p) => {
    if (!p?.id) return;
    setSubjectType("project");
    setSelected({
      id: p.id,
      label: p.name || p.displayTitle || `#${p.id}`,
    });
  };

  // Keep subject selection so SuggestedTodosConfirm can still tag project/customer
  // after publish. Composer resets its own fields; do not remount (would wipe todos).
  const handleCreated = () => {
    // Invalidate any in-flight availability probes started before this success.
    requestIdRef.current += 1;
    setUnavailable(false);
    setJustPosted(true);
    setTimeout(() => setJustPosted(false), 3000);
  };

  return (
    <div className={dashboardDesign ? "pd-card pd-composer" : "rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.06)]"}>
      <div className="mb-3 flex items-center gap-2">
        <PencilSquareIcon className="h-5 w-5 text-[#1463C7]" />
        <h3 className="text-base font-semibold text-slate-900">{t(dashboardDesign ? "dashboardDesign.writeUpdate" : "Skriv oppdatering")}</h3>
        {justPosted && !unavailable && (
          <span className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-emerald-600">
            <CheckCircleIcon className="h-5 w-5" />
            {t("Publisert")}
          </span>
        )}
      </div>

      {unavailable ? (
        <StatusUpdateUnavailableNotice onRetry={probeAvailability} retrying={probing} />
      ) : (
        <>
          {!dashboardDesign && <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="inline-flex overflow-hidden rounded-full border border-slate-200 bg-slate-50">
              {SUBJECT_TYPES.map(({ key, labelKey, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => chooseType(key)}
                  title={t(labelKey)}
                  aria-label={t(labelKey)}
                  aria-pressed={subjectType === key}
                  className={TWclassNames(
                    "flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors",
                    subjectType === key
                      ? "bg-[#1463C7] text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className={dashboardDesign ? "sr-only" : undefined}>{t(labelKey)}</span>
                </button>
              ))}
            </div>

            {subjectType && (
              <button
                type="button"
                onClick={() => setOpenSelector(subjectType)}
                className={TWclassNames(
                  "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                  selected
                    ? "border-[#1463C7] bg-[#EAF3FD] text-[#0B3A78]"
                    : "border-slate-300 border-dashed text-slate-500 hover:bg-slate-50"
                )}
              >
                {selected ? selected.label : t("Velg…")}
              </button>
            )}
          </div>}

          <StatusUpdateComposer
            iconToolbar={dashboardDesign}
            toolbarSlot={dashboardDesign ? SUBJECT_TYPES.map(({ key, labelKey, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => chooseType(key)}
                title={t(labelKey)}
                aria-label={t(labelKey)}
                aria-pressed={subjectType === key}
                className="pd-composer-tool"
              >
                <Icon className="h-[18px] w-[18px]" />
              </button>
            )) : null}
            subjectType={subjectType}
            subjectId={selected?.id}
            projectId={subjectType === "project" ? selected?.id : null}
            project={
              subjectType === "project" && selected
                ? { id: selected.id, displayTitle: selected.label }
                : null
            }
            onCreated={handleCreated}
            onAcceptSuggestedProject={handleAcceptSuggestedProject}
            onModuleUnavailable={() => setUnavailable(true)}
          />

          {dashboardDesign && subjectType && (
            <button
              type="button"
              onClick={() => setOpenSelector(subjectType)}
              className="pd-composer-subject"
              title={t(subjectType === "project" ? "Prosjekt" : "Kunde")}
            >
              {t(subjectType === "project" ? "Prosjekt" : "Kunde")}: {selected ? selected.label : t("Velg…")}
            </button>
          )}

          {!selected && !dashboardDesign && (
            <p className="mt-2 text-xs text-slate-500">{t("oppdatering.subject_optional_hint")}</p>
          )}

          <ProjectSelector
            showField={false}
            open={openSelector === "project"}
            onClose={() => setOpenSelector(null)}
            onChange={handleSelected}
          />
          <CustomerSelector
            showField={false}
            open={openSelector === "customer"}
            onClose={() => setOpenSelector(null)}
            onChange={handleSelected}
          />
        </>
      )}
    </div>
  );
}

export default HomeStatusComposer;
